<template>
  <div fluid class="fill-height">
    <v-btn
      style="z-index: 4; bottom: 60px"
      rounded
      fixed
      bottom
      right
      color="primary"
      @click="openAddRegisterForm"
      v-if="$vuetify.breakpoint.smAndDown"
    >
      <v-icon>mdi-plus</v-icon>
      <span>{{ $t(`$vuetify.pointOfSale.addRegisterBtn`) }}</span>
    </v-btn>
    <v-container
      fluid
      class="d-flex justify-center align-start flex-wrap fill-height"
      style="position: relative"
    >
      <v-row>
        <v-spacer></v-spacer>
        <Search-box
          :emitDestination="searchEmitDestination"
          searchPlaceholder="Pretraži blagajne"
        ></Search-box>
      </v-row>
      <v-row
        class="d-flex justify-center align-center fill-height"
        style="position: absolute"
      >
        <v-card
          class="glass-card align-center justify-self-center ma-5 mt-0"
          v-for="(register, idx) in currentRegisters"
          :key="register.id"
          hover
          height="320"
          width="280"
          @click="openRegister(register)"
          @keypress.enter.prevent="openRegister(register)"
          :ref="`register${idx}`"
        >
          <!-- Glass morphism header with icon -->
          <div class="glass-header d-flex flex-column align-center justify-center">
            <div class="header-gloss"></div>
            <div class="icon-wrapper">
              <v-icon size="64" color="white">mdi-cash-register</v-icon>
            </div>
            <div class="status-badge" :class="getRegisterStatusClass(register.status)">
              {{ getRegisterStatusText(register.status) }}
            </div>
            <div v-if="register.reserved_by" class="reserved-badge">
              <v-icon small color="white">mdi-account</v-icon>
              <span class="ml-1">{{ register.reserved_by }}</span>
            </div>
          </div>

          <!-- Content section -->
          <div class="glass-content pa-4">
            <div class="content-text">
              <h3 class="register-name mb-3">{{ register.name }}</h3>

              <div v-if="register.number" class="info-row d-flex align-center mb-2">
                <v-icon size="16" class="mr-2" color="primary">mdi-numeric</v-icon>
                <span class="info-text">Broj: {{ register.number }}</span>
              </div>

              <div v-if="register.type" class="info-row d-flex align-center">
                <v-icon size="16" class="mr-2" color="primary">mdi-cash-register</v-icon>
                <span class="info-text">{{ getRegisterTypeText(register.type) }}</span>
              </div>
            </div>

            <div class="arrow-indicator">
              <v-icon size="28">mdi-arrow-right-circle</v-icon>
            </div>
          </div>

          <!-- Actions footer -->
          <v-divider class="glass-divider"></v-divider>
          <v-card-actions class="glass-actions pa-3">
            <v-spacer></v-spacer>
            <v-menu offset-y nudge-left="145" v-if="!hideMenu">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @keypress.enter.stop=""
                  class="menu-btn"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="glass-menu">
                <v-list-item
                  v-for="(item, index) in registerOptions"
                  :key="index"
                  style="cursor: pointer"
                  @click="`${item.action(register)}`"
                  @keypress.enter.prevent="`${item.action(register)}`"
                  :disabled="register.status === 'PROCESSING'"
                >
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-card-actions>
        </v-card>
        <div class="add-dashed-card ma-5" style="height: 320px; width: 280px;" @click="openAddRegisterForm">
          <v-icon class="add-dashed-icon">mdi-plus</v-icon>
          <span class="add-dashed-text">Dodaj novu blagajnu</span>
        </div>
      </v-row>
    </v-container>
    <add-cash-register
      ref="cashRegisterForm"
      @success="refreshRegisters"
    ></add-cash-register>
    <edit-cash-register
      ref="editCashRegisterForm"
      @editSuccess="refreshEditedRegister"
    ></edit-cash-register>
    <edit-register-receipt-settings
      ref="editRegisterReceiptSettings"
      @editSuccess="refreshEditedRegister"
    ></edit-register-receipt-settings>
    <user-selection
      ref="userSelection"
      @userSelectSuccess="userSelected"
    ></user-selection>
    <pin-login ref="pinLogin" @pinLoginSuccess="userSelected"></pin-login>
    <education-mode ref="educationMode"></education-mode>
  </div>
</template>
<script>

import { auth, df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import EventBus from '@/plugins/event-bus'
import state from '@/state'
import applicationSettings from '@/mixins/applicationSettings'
import LogData from '@/model/LogData'
import log from '@/mixins/log'

export default {
  components: {
    AddCashRegister: () => import('@/modules/point-of-sale/components/AddCashRegister'),
    EditCashRegister: () => import('@/modules/point-of-sale/components/EditCashRegister'),
    EditRegisterReceiptSettings: () => import('@/modules/point-of-sale/components/EditCashRegisterReceiptSettings'),
    UserSelection: () => import('@/modules/point-of-sale/components/UserSelection'),
    PinLogin: () => import('@/modules/point-of-sale/components/PinLogin'),
    EducationMode: () => import('@/modules/point-of-sale/components/EducationMode')
  },
  inject: ['showMsgBox', 'confirm', 'confirmClose', 'showLoader', 'hideLoader', 'showMsgBoxHtml'],
  data: vm => ({
    registers: [],
    currentRegisters: [],
    cashRegUsers: [],
    listeners: [],
    registeredListenerIds: new Set(),
    hideMenu: false,
    registerIds: [],
    registerOptions: [
      { title: 'Postavke blagajne', action: vm.openEditRegisterForm },
      { title: 'Postavke računa blagajne', action: vm.editRegisterReceiptSettings }
      // { title: 'Postavke edukacijskog moda', action: vm.educationMode }
    ],
    searchEmitDestination: 'searchCashRegisters'
  }),
  mixins: [applicationSettings, log],
  computed: {
    search () {
      return state.filter
    }
  },
  created () {
    this.pendingReservedBy = {}
    this.rafPending = false

    const docRef = df.doc(`user_cash_registers/${auth.currentUser.uid}.${state.getCurrentCompany().id}.${state.getPointOfSale().id}`)
    const listener = docRef.onSnapshot((doc) => {
      const data = doc.data()
      if (data?.cash_registers) {
        this.registers = Object.keys(data.cash_registers)
          .map(key => data.cash_registers[key])
          .filter(register => register?.resource_status === 'ACTIVE')
          .sort((a, b) => {
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          })

        this.applySearch()

        if (this.registers?.length > 0) {
          state.registerIds = this.registers.map(r => r.number)

          this.registers.forEach((register) => {
            if (this.registeredListenerIds.has(register.id)) return
            this.registeredListenerIds.add(register.id)

            const regListener = df.doc(`cash_registers/${register.id}`).onSnapshot((regDoc) => {
              const regData = regDoc.data()
              if (!regData) return

              this.pendingReservedBy[register.id] = regData.reserved_by || ''

              if (!this.rafPending) {
                this.rafPending = true
                requestAnimationFrame(() => {
                  Object.entries(this.pendingReservedBy).forEach(([id, reservedBy]) => {
                    const ri = this.registers.findIndex(r => r.id === id)
                    if (ri !== -1) this.registers[ri].reserved_by = reservedBy

                    const ci = this.currentRegisters.findIndex(r => r.id === id)
                    if (ci !== -1) this.$set(this.currentRegisters, ci, { ...this.currentRegisters[ci], reserved_by: reservedBy })
                  })
                  this.pendingReservedBy = {}
                  this.rafPending = false
                })
              }
            })
            this.listeners.push(regListener)
          })
        } else {
          state.registerIds = []
        }
      } else {
        state.registerIds = []
      }
    })
    this.listeners.push(listener)
  },
  mounted () {
    EventBus.$on(this.searchEmitDestination, (searchObject) => {
      this.searchAll(searchObject.searchString)
    })
    const user = state.getUser()

    if (!user.roles.ADMIN && !user.roles.SUPERADMIN) {
      this.hideMenu = true
    }
    try {
      this.$refs.register0[0].$el.focus()
    } catch {
      setTimeout(() => {
        if (this.$refs.register0 && this.$refs.register0[0]) {
          this.$refs.register0[0].$el.focus()
        }
      }, 500)
    }
  },
  methods: {
    getRegisterStatusClass (status) {
      if (status === 'OK') return 'status-ok'
      if (status === 'PROCESSING') return 'status-processing'
      return 'status-default'
    },
    getRegisterStatusText (status) {
      if (status === 'OK') return 'Aktivna'
      if (status === 'PROCESSING') return 'U obradi'
      return status || 'Nepoznato'
    },
    getLoginTypeText (loginType) {
      if (!loginType || loginType === 'DEFAULT') return 'Standardna prijava'
      if (loginType === 'SELECT_USER') return 'Odabir korisnika'
      if (loginType === 'PINCODE') return 'PIN prijava'
      return loginType
    },
    getRegisterTypeText (registerType) {
      if (registerType === 'MP') return this.$t('$vuetify.cashRegister.registerTypes.mp')
      if (registerType === 'VP') return this.$t('$vuetify.cashRegister.registerTypes.vp')
      if (registerType === 'ALL') return 'Maloprodajna/Veleprodajna'
      return registerType
    },
    applySearch (val) {
      const term = val !== undefined ? val : (this.search || '')
      const source = term !== ''
        ? this.registers.filter(r => r.name.toLowerCase().includes(term.toLowerCase()))
        : this.registers
      this.currentRegisters = source.slice().sort((a, b) => a.name.localeCompare(b.name))
    },
    searchAll (val) {
      this.applySearch(val)
    },
    userSelected (data) {
      data.cashReg.reserving = data.user.name + ' ' + data.user.surname
      state.setCashRegisterUser(data.user)

      this.callReservation(data.cashReg, false)
    },
    openUserSelection (users, cashReg) {
      this.$refs.userSelection.open(users, cashReg)
    },
    openPincodeLogin (users, cashReg) {
      this.$refs.pinLogin.open(users, cashReg)
    },
    async getCashRegister (id) {
      const register = await df.doc(`cash_registers/${id}`).get()
      return register
    },
    async getUser () {
      const user = await df.doc(`users/${auth.currentUser.uid}`).get()
      return user
    },
    async getCashRegUsers (id) {
      const query = await df.collection(`cash_registers/${id}/users`)
      var cashRegUsers = []
      query.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          cashRegUsers.push(doc.data())
        })
        return cashRegUsers
      })
    },
    openRegister (register) {
      if (register.status !== 'OK') {
        return
      }

      this.getCashRegister(register.id).then(async (regResp) => {
        const cashReg = regResp.data()

        if (cashReg.login_type === undefined || cashReg.login_type === '' || cashReg.login_type === 'DEFAULT') {
          this.getUser().then((userResp) => {
            const user = userResp.data()
            if (!('PRODAVAČ' in user.roles)) {
              this.showMsgBoxHtml({ text: 'Nemate privilegiju prodavača na ovom prodajnom mjestu, te niste u mogućnosti izdavati račune.<br/>Da biste dodali privilegiju prodavača odaberite<br/> Postavke → Korisnici te za korisnika odaberite privilegiju “PRODAVAČ”.', actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
              return
            }
            cashReg.reserving = ''
            cashReg.reserving = user.name + ' ' + user.surname

            if (cashReg.reserved_by !== undefined && cashReg.reserved_by !== '') {
              if (cashReg.reserved_by !== user.name + ' ' + user.surname) {
                this.confirm({
                  title: 'Rezervacija blagajne',
                  message: `Trenutno je na blagajnu prijavljen ${
                   cashReg.reserved_by
                  }.<br>Da li se želite odjaviti tog korisnika i prijaviti sebe?`,
                  options: {
                    toolbar: true,
                    width: 410,
                    confirmText: 'DA',
                    cancelText: this.$t('$vuetify.close')
                  }
                }).then(async (resp) => {
                  if (resp) {
                    state.setCashRegisterUser(user)
                    this.callReservation(cashReg, true)
                  } else {
                    this.confirmClose()
                  }
                })
              } else {
                state.setCashRegisterUser(user)
                if (state.getCurrentCompany().save_log) {
                  this.prepareLogCall()
                }
                state.setCashRegister(cashReg)
                this.$router.push({
                  name: 'cashRegister',
                  params: {
                    companyId: state.getCurrentCompany().id,
                    locationId: state.getPointOfSale().id,
                    registerId: state.getCashRegister().id
                  }
                })
              }
            } else if (cashReg.reserved_by === undefined || cashReg.reserved_by === '') {
              state.setCashRegisterUser(user)
              this.callReservation(cashReg, false)
            }
          })
        } else if (cashReg.login_type === 'SELECT_USER') {
          const snapshot = await df.collection(`cash_registers/${cashReg.id}/users`).get()
          const cashRegUsers = []

          snapshot.forEach((doc) => {
            cashRegUsers.push(doc.data())
          })

          if (cashRegUsers.length === 1 && cashRegUsers[0].user.id === auth.currentUser.uid) {
            state.setCashRegisterUser(cashRegUsers[0].user)
            cashReg.reserving = cashRegUsers[0].user.name + ' ' + cashRegUsers[0].user.surname
            this.callReservation(cashReg, false)
          } else {
            this.openUserSelection(cashRegUsers, cashReg)
          }
        } else if (cashReg.login_type === 'PINCODE') {
          const snapshot = await df.collection(`cash_registers/${cashReg.id}/users`).get()
          const cashRegUsers = []

          snapshot.forEach((doc) => {
            cashRegUsers.push(doc.data())
          })

          if (cashRegUsers.length === 1 && cashRegUsers[0].user.id === auth.currentUser.uid) {
            state.setCashRegisterUser(cashRegUsers[0].user)
            cashReg.reserving = cashRegUsers[0].user.name + ' ' + cashRegUsers[0].user.surname
            this.callReservation(cashReg, false)
          } else {
            this.openPincodeLogin(cashRegUsers, cashReg)
          }
        }
      })
    },
    callReservation (register, closeConfirm) {
      const that = this
      if (closeConfirm) {
        that.confirmClose()
      }

      this.showLoader()

      if (register.reserved_by !== undefined) {
        df.doc(`cash_registers/${register.id}`).update({ reserved_by: register.reserving })
          .then(() => {
          })
          .catch(() => {
            that.hideLoader()
            that.showMsgBox({ text: 'Pogreška prilikom zapisa podataka', actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
          })
          .finally(() => {
            that.hideLoader()
            state.setCashRegister(register)
            that.$router.push({
              name: 'cashRegister',
              params: {
                companyId: state.getCurrentCompany().id,
                locationId: state.getPointOfSale().id,
                registerId: state.getCashRegister().id
              }
            })
          })
      }

      if (state.getCurrentCompany().save_log) {
        this.prepareLogCall()
      }
    },
    prepareLogCall () {
      var req = new LogData()

      let userId = auth.currentUser.uid
      if (state.getCashRegisterUser().id && state.getCashRegisterUser().id !== '') {
        userId = state.getCashRegisterUser().id
      }

      req.action_name = 'Prijava'
      req.action_id = '1'
      req.user_id = userId

      this.setLog(req)
    },
    openAddRegisterForm () {
      this.$refs.cashRegisterForm.open()
    },
    openEditRegisterForm (register) {
      this.$refs.editCashRegisterForm.open(register)
    },
    editRegisterReceiptSettings (register) {
      this.$refs.editRegisterReceiptSettings.open(register)
    },
    educationMode (register) {
      this.$refs.educationMode.open(register)
    },
    refreshRegisters (register) {
      // this.registers.push({ name: register.name, status: 'PROCESSING' })
      this.currentRegisters.push({ name: register.name, status: 'PROCESSING' })
      this.currentRegisters = this.currentRegisters.sort((a, b) => a.name.localeCompare(b.name))
    },
    refreshEditedRegister (register) {
      this.currentRegisters = this.currentRegisters.map(reg => {
        if (reg.id === register.id) {
          reg.name = register.name
          reg.status = 'PROCESSING'
        }
        return reg
      })
    }
  },
  beforeDestroy () {
    EventBus.$off(this.searchEmitDestination)
    // Unsubscribe from all listeners
    this.listeners.forEach(listener => {
      if (typeof listener === 'function') {
        listener()
      }
    })
    this.listeners = []
  }
}
</script>

<style scoped>
/* Glass Morphism Card */
.glass-card {
  border-radius: 24px !important;
  background: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.3s ease !important;
  cursor: pointer !important;
  max-width: 400px !important;
}

.glass-card::before {
  opacity: 0 !important;
}

.glass-card:hover {
  transform: translateY(-6px) scale(1.01);
  background: rgba(255, 255, 255, 0.85) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12) !important;
  border-color: rgba(0, 179, 190, 0.4) !important;
}

/* Glass Header */
.glass-header {
  position: relative;
  height: 140px;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%);
  overflow: hidden;
}

.header-gloss {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.22) 50%, transparent 100%);
  transition: left 0.5s ease;
  pointer-events: none;
  z-index: 1;
}

.glass-card:hover .header-gloss {
  left: 150%;
}

.glass-card:hover .glass-header {
  filter: brightness(1.1);
}

/* Icon Wrapper */
.icon-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.3s ease,
              box-shadow 0.3s ease;
  will-change: transform;
}

.glass-card:hover .icon-wrapper {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.3);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.25),
    inset 0 2px 6px rgba(255, 255, 255, 0.4);
}

.icon-wrapper .v-icon {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: filter 0.3s ease;
}

.glass-card:hover .icon-wrapper .v-icon {
  filter: drop-shadow(0 5px 10px rgba(0, 0, 0, 0.35));
}

/* Status Badge */
.status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.status-ok {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.status-processing {
  background: rgba(255, 152, 0, 0.9);
  color: white;
}

.status-default {
  background: rgba(158, 158, 158, 0.9);
  color: white;
}

.glass-card:hover .status-badge {
  transform: scale(1.03);
}

/* Reserved Badge */
.reserved-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  background: rgba(33, 150, 243, 0.9);
  color: white;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.glass-card:hover .reserved-badge {
  transform: scale(1.03);
}

/* Content Section */
.glass-content {
  background: transparent;
  min-height: 110px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.content-text {
  flex: 1;
  min-width: 0;
}

.arrow-indicator {
  flex-shrink: 0;
  margin-left: 10px;
  opacity: 0.6;
  transform: translateX(0);
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.arrow-indicator .v-icon {
  color: #9e9e9e !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover .arrow-indicator {
  opacity: 1;
  transform: translateX(4px);
}

.glass-card:hover .arrow-indicator .v-icon {
  color: #00bcd4 !important;
  filter: drop-shadow(0 0 8px rgba(0, 188, 212, 0.5));
}

.register-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
  text-shadow: 0 2px 4px rgba(255, 255, 255, 0.8);
  text-align: center;
}

.glass-card:hover .register-name {
  color: #00b3be;
}

/* Info Rows */
.info-row {
  font-size: 13px;
  color: #555;
  transition: color 0.3s ease;
}

.info-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.glass-card:hover .info-row {
  color: #333;
}

.info-row .v-icon {
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.glass-card:hover .info-row .v-icon {
  opacity: 1;
}

/* Glass Divider */
.glass-divider {
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(0, 179, 190, 0.3) 50%,
    transparent 100%);
  height: 1px;
  border: none;
}

/* Actions */
.glass-actions {
  min-height: 48px;
  background: rgba(255, 255, 255, 0.5);
}

.menu-btn {
  background: rgba(255, 255, 255, 0.6) !important;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.3s ease !important;
  will-change: transform;
}

.menu-btn:hover {
  background: rgba(0, 179, 190, 0.1) !important;
  transform: scale(1.1) rotate(90deg);
}

/* Glass Menu */
.glass-menu {
  background: rgba(255, 255, 255, 0.98) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

/* Legacy styles */
.one-liner-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.add-dashed-card {
  background-color: #FAFAFA;
  border: 2px dashed #CBD5E1;
  border-radius: 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
}

.add-dashed-card:hover {
  border-color: #00BCD4;
}

.add-dashed-card:hover .add-dashed-icon {
  color: #00BCD4 !important;
}

.add-dashed-card:hover .add-dashed-text {
  color: #00BCD4 !important;
}

.add-dashed-icon {
  font-size: 28px !important;
  color: #CBD5E1 !important;
  transition: color 0.2s ease;
}

.add-dashed-text {
  font-size: 14px;
  font-weight: 500;
  color: #94A3B8;
  transition: color 0.2s ease;
  text-align: center;
  padding: 0 16px;
}
</style>
